import {
    DragHandleDots2Icon,
    EyeNoneIcon,
    EyeOpenIcon,
    MixerHorizontalIcon,
} from '@radix-ui/react-icons';
import {
    Box,
    Button,
    Flex,
    IconButton,
    Popover,
    ScrollArea,
    Separator,
    Text,
} from '@radix-ui/themes';
import { Column, Table } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface TableColumnControlPanelProps<TData> {
    table: Table<TData>;
    canReOrder?: boolean;
}

interface ColumnToggleItemProps<TData> {
    column: Column<TData, unknown>;
    canReOrder: boolean;
}

const ColumnToggleItem = <TData,>({
    column,
    canReOrder,
}: ColumnToggleItemProps<TData>) => {
    const isVisible = column.getIsVisible();
    const label = column.columnDef.header?.toString();

    return (
        <Flex gap='3' align={'center'} p='1'>
            {canReOrder && (
                <DragHandleDots2Icon
                    style={{
                        cursor: 'grab',
                        flexShrink: 0,
                        color: 'var(--gray-a9)',
                    }}
                />
            )}

            <Box flexGrow={'1'} px={'1'}>
                <Text size='2'>{label}</Text>
            </Box>

            <IconButton
                size={'2'}
                radius={'full'}
                variant={'ghost'}
                color={'gray'}
                onClick={() => column.toggleVisibility(!isVisible)}
            >
                {isVisible ? <EyeOpenIcon /> : <EyeNoneIcon />}
            </IconButton>
        </Flex>
    );
};

export const TableColumnControlPanel = <TData extends object>({
    table,
    canReOrder = false,
}: TableColumnControlPanelProps<TData>) => {
    const { t } = useTranslation('table');
    const controllableColumns = useMemo(
        () =>
            table
                .getAllColumns()
                .filter(
                    (column) => column.id !== 'select' && column.getCanHide(),
                ),
        [table],
    );

    return (
        <Popover.Root modal={false}>
            <Popover.Trigger>
                <IconButton
                    size={'2'}
                    color={'gray'}
                    variant={'ghost'}
                    radius={'full'}
                    style={{
                        padding: 'var(--space-2)',
                    }}
                    asChild
                >
                    <MixerHorizontalIcon height={'20px'} width={'20px'} />
                    {/* {t('configureColumns')} */}
                </IconButton>
            </Popover.Trigger>

            <Popover.Content
                side={'left'}
                style={{ padding: 0 }}
                minWidth={'250px'}
            >
                <Flex direction='column'>
                    <Box p={'2'}>
                        <Text size='3' weight='bold'>
                            {t('manageColumns')}
                        </Text>
                    </Box>

                    <Separator size={'4'} />

                    <Flex
                        direction='column'
                        gap='1'
                        pr={'2'}
                        maxHeight={'150px'}
                        asChild
                    >
                        <ScrollArea scrollbars={'vertical'} type='scroll'>
                            {controllableColumns.map((column) => (
                                <ColumnToggleItem
                                    key={column.id}
                                    column={column}
                                    canReOrder={canReOrder}
                                />
                            ))}
                        </ScrollArea>
                    </Flex>
                    <Separator size={'4'} />

                    <Flex justify='end' p='2'>
                        <Popover.Close>
                            <Button variant='soft' size='1'>
                                {t('common:buttons.close')}
                            </Button>
                        </Popover.Close>
                    </Flex>
                </Flex>
            </Popover.Content>
        </Popover.Root>
    );
};
